/**
 * Field rules for a Prozo label's parties, package and money.
 *
 * Mirrors suspire-server `orders/shipping_validation.py` rule for rule and
 * message for message, so the wizard refuses exactly what the server would.
 * The server stays the guard; this exists for an instant, field-level message
 * and to stop the operator moving past a step that would fail at Generate
 * Label.
 *
 * Prozo publishes no field contract, so the rules follow what it has accepted
 * and refused in production: a 10-digit Indian mobile (an optional +91/0
 * prefix is stripped), a 6-digit pincode not starting with 0, and a pickup
 * email on forward legs ("From Email is required").
 *
 * Every function returns a message, or null when the value is fine.
 */

const PHONE_RE = /^[6-9]\d{9}$/;
// Letters are refused rather than stripped, so "98765abc43210" is not quietly
// read as a valid number.
const PHONE_INPUT_RE = /^[+\d\s\-().]*$/;
const PINCODE_RE = /^[1-9]\d{5}$/;
const GSTIN_RE = /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const HSN_RE = /^\d{4}(\d{2}){0,2}$/;
const PLACE_RE = /^[A-Za-z][A-Za-z .,'&()-]*$/;
// Same shape Django's EmailValidator accepts for ordinary addresses.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const NAME_MIN_LENGTH = 2;
export const NAME_MAX_LENGTH = 255;
export const ADDRESS_MIN_LENGTH = 10;
export const PLACE_MAX_LENGTH = 100;

const text = (value) => (value === null || value === undefined ? '' : String(value).trim());

/** The 10-digit form of a typed number: separators and +91/91/0 removed. */
export function normalizePhone(raw) {
  const digits = text(raw).replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('91')) return digits.slice(2);
  if (digits.length === 11 && digits.startsWith('0')) return digits.slice(1);
  return digits;
}

export function phoneError(raw, label = 'Phone number') {
  const value = text(raw);
  if (!value) return `${label} is required.`;
  if (!PHONE_INPUT_RE.test(value) || !PHONE_RE.test(normalizePhone(value))) {
    return `${label} must be a 10-digit Indian mobile number starting with 6, 7, 8 or 9 (got '${value}').`;
  }
  return null;
}

export function nameError(raw, label = 'Name') {
  const value = text(raw);
  if (!value) return `${label} is required.`;
  if (value.length < NAME_MIN_LENGTH) return `${label} must be at least ${NAME_MIN_LENGTH} characters.`;
  if (value.length > NAME_MAX_LENGTH) return `${label} must be at most ${NAME_MAX_LENGTH} characters.`;
  if (!/[A-Za-z]/.test(value)) return `${label} must contain letters (got '${value}').`;
  // The mistake that prompted these rules: a phone number in the name box.
  if (value.replace(/\D/g, '').length >= 7) {
    return `${label} looks like a phone number (got '${value}') — enter the person's name.`;
  }
  return null;
}

export function emailError(raw, label = 'Email', { required = false } = {}) {
  const value = text(raw);
  if (!value) return required ? `${label} is required.` : null;
  if (!EMAIL_RE.test(value)) return `${label} must be a valid email address (got '${value}').`;
  return null;
}

export function pincodeError(raw, label = 'Pincode') {
  const value = text(raw);
  if (!value) return `${label} is required.`;
  if (!PINCODE_RE.test(value)) return `${label} must be 6 digits and cannot start with 0 (got '${value}').`;
  return null;
}

export function placeError(raw, label) {
  const value = text(raw);
  if (!value) return `${label} is required.`;
  if (value.length > PLACE_MAX_LENGTH) return `${label} must be at most ${PLACE_MAX_LENGTH} characters.`;
  if (!PLACE_RE.test(value)) {
    return `${label} may only contain letters, spaces and . , ' & ( ) - (got '${value}').`;
  }
  return null;
}

export function addressError(line1, line2 = '', landmark = '') {
  if (!text(line1)) return 'Address line 1 is required.';
  const joined = [text(line1), text(line2), text(landmark)].filter(Boolean).join(' ');
  if (joined.length < ADDRESS_MIN_LENGTH) {
    return `Address is too short for a courier to find — at least ${ADDRESS_MIN_LENGTH} characters across the address lines and landmark.`;
  }
  return null;
}

/** Optional; when given it must be a well-formed 15-character GSTIN. */
export function gstinError(raw, label = 'GSTIN') {
  const value = text(raw).toUpperCase();
  if (!value) return null;
  if (!GSTIN_RE.test(value)) {
    return `${label} must be a valid 15-character GSTIN, e.g. 27ABCDE1234F1Z5 (got '${value}').`;
  }
  return null;
}

/** Optional; 4, 6 or 8 digits. Dots are allowed ("7113.11.10") and stripped before Prozo. */
export function hsnError(raw) {
  const value = text(raw);
  if (!value) return null;
  if (!HSN_RE.test(value.replace(/\./g, ''))) {
    return `HSN code must be 4, 6 or 8 digits, dots allowed (got '${value}').`;
  }
  return null;
}

export function positiveNumberError(raw, label) {
  const value = text(raw);
  const n = Number(value);
  if (!value || !Number.isFinite(n)) return `${label} must be a number.`;
  if (n <= 0) return `${label} must be greater than zero.`;
  return null;
}

/** A COD amount must be positive and no more than the declared value. */
export function codAmountError(codAmount, invoiceValue) {
  const error = positiveNumberError(codAmount, 'COD amount');
  if (error) return error;
  const invoice = Number(text(invoiceValue));
  if (Number.isFinite(invoice) && Number(text(codAmount)) > invoice) {
    return `COD amount (${text(codAmount)}) cannot exceed the invoice value (${text(invoiceValue)}).`;
  }
  return null;
}

/**
 * Every problem with one consignor/consignee block, keyed by party field
 * (name, phone, email, address_line_1, city, state, pincode, gstin).
 * `who` prefixes the messages ("Pickup", "Customer").
 */
export function partyErrors(party, { requireEmail = false, who = '' } = {}) {
  const p = party || {};
  const prefix = who ? `${who} ` : '';
  const checks = {
    name: nameError(p.name, `${prefix}name`),
    phone: phoneError(p.phone, `${prefix}phone`),
    email: emailError(p.email, `${prefix}email`, { required: requireEmail }),
    address_line_1: addressError(p.address_line_1, p.address_line_2, p.landmark),
    city: placeError(p.city, `${prefix}city`),
    state: placeError(p.state, `${prefix}state`),
    pincode: pincodeError(p.pincode, `${prefix}pincode`),
    gstin: gstinError(p.gstin, `${prefix}GSTIN`),
  };
  return Object.fromEntries(Object.entries(checks).filter(([, message]) => message));
}

/** Weight and dimensions, keyed weight_g / length_cm / breadth_cm / height_cm. */
export function packageErrors(pkg) {
  const p = pkg || {};
  const checks = {
    weight_g: positiveNumberError(p.weight_g, 'Weight (g)'),
    length_cm: positiveNumberError(p.length_cm, 'Length (cm)'),
    breadth_cm: positiveNumberError(p.breadth_cm, 'Breadth (cm)'),
    height_cm: positiveNumberError(p.height_cm, 'Height (cm)'),
  };
  return Object.fromEntries(Object.entries(checks).filter(([, message]) => message));
}

/** Prefix every key of an error map: prefixKeys({phone: '…'}, 'pickup') → {'pickup.phone': '…'}. */
export function prefixKeys(errors, prefix) {
  return Object.fromEntries(Object.entries(errors || {}).map(([k, v]) => [`${prefix}.${k}`, v]));
}
